import React, { useMemo } from 'react';

import { getFileFieldDisplayName } from '@aline/graphql';
import { Typography } from '@mui/material';
import { GenericPropertyIcon } from 'src/components/icons/GenericPropertyIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const INPUT_CHIP_ICON_SIZE = 12;

const useStyles = makeStyles()(theme => ({
  chip: {
    alignItems: 'center',
    backgroundColor: theme.appColors.selectedBackground,
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
  },
  chipText: {
    fontSize: 12,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

type QuestionInput = Pick<Apollo.ExtractionQuestion, 'id' | 'title' | '__typename'>;

interface QuestionInputChipsProps {
  inputs: QuestionInput[];
  inputFileFields?: Apollo.ReportColumnFileField[];
}

export function QuestionInputChips({
  inputFileFields = [],
  inputs,
}: QuestionInputChipsProps): JSX.Element {
  const { classes } = useStyles();
  const sortedChips = useMemo(() => {
    const allChips: { input: QuestionInputChipProps['input']; key: string; label: string }[] = [
      ...inputs.map(q => ({ input: q, key: q.id, label: q.title || 'Untitled' })),
      ...inputFileFields.map(f => ({
        input: { field: f },
        key: f,
        label: getFileFieldDisplayName(f),
      })),
    ];
    return allChips.sort((a, b) => a.label.localeCompare(b.label));
  }, [inputFileFields, inputs]);

  if (!sortedChips.length) return <QuestionInputChip input="file" />;

  return (
    <div className={classes.chips}>
      {sortedChips.map(chip => (
        <QuestionInputChip key={chip.key} input={chip.input} />
      ))}
    </div>
  );
}

interface QuestionInputChipProps {
  input: QuestionInput | 'file' | { field: Apollo.ReportColumnFileField };
}

export function QuestionInputChip({ input }: QuestionInputChipProps): JSX.Element {
  if (input === 'file')
    return <InputChip icon={<QuestionInputChipIcon variant="file" />} label="File" />;

  if ('field' in input)
    return (
      <InputChip
        icon={<QuestionInputChipIcon variant="file" />}
        label={getFileFieldDisplayName(input.field)}
      />
    );

  return (
    <InputChip icon={<QuestionInputChipIcon variant="ai" />} label={input.title || 'Untitled'} />
  );
}

interface InputChipProps {
  icon: React.ReactNode;
  label: string;
  title?: string;
}

export function InputChip({ icon, label, title }: InputChipProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.chip} title={title}>
      {icon}
      <Typography noWrap className={classes.chipText} color="primary">
        {label}
      </Typography>
    </div>
  );
}

export const useInputChipIconStyles = makeStyles()(_theme => ({
  icon: {
    flexShrink: 0,
    height: INPUT_CHIP_ICON_SIZE,
    objectFit: 'contain',
    width: INPUT_CHIP_ICON_SIZE,
  },
}));

interface QuestionInputChipIconProps {
  variant: 'ai' | 'file';
}

function QuestionInputChipIcon({ variant }: QuestionInputChipIconProps): JSX.Element {
  const { classes } = useInputChipIconStyles();

  switch (variant) {
    case 'ai':
      return <SmallAiSparkleIcon className={classes.icon} fill={IconColors.Primary} />;
    case 'file':
      return <GenericPropertyIcon className={classes.icon} />;
  }
}
